(() => {
  if (customElements.get('sampler-product-contents')) return;

  const query = `query SamplerProductContents($id: ID!, $country: CountryCode) @inContext(country: $country) {
    node(id: $id) {
      ... on ProductVariant {
        ...SamplerProductRecipe
        product { id variants(first: 10) { nodes { ...SamplerProductRecipe } } }
      }
    }
  }
  fragment SamplerProductRecipe on ProductVariant {
        id
        components(first: 30) {
          pageInfo { hasNextPage }
          nodes { quantity productVariant { product { id title } } }
        }
  }`;

  class SamplerProductContents extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      if (document.documentElement.classList.contains('judgeme-review-only-mode') ||
          new URLSearchParams(window.location.search).get('judgeme_review_only') === '1') {
        this.hidden = true;
        return;
      }
      this.info = this.closest('product-info');
      this.list = this.querySelector('.sampler-product-contents__list');
      this.status = this.querySelector('.sampler-product-contents__status');
      this.retry = this.querySelector('.sampler-product-contents__retry');
      if (!this.info || !this.list || !this.status || !this.retry) return;
      this.initialized = true;
      this.cache = new Map();
      this.onVariantChange = (event) => {
        // product-info dispatches this after updating the actual purchase variant.
        // Listen only: never write the form, options, price, or cart.
        if (event.target.matches('input[name="id"]') && event.target.closest('product-info') === this.info) {
          this.load(event.target.value);
        }
      };
      this.onRetry = () => this.load(this.activeVariantId, true);
      this.info.addEventListener('change', this.onVariantChange);
      this.retry.addEventListener('click', this.onRetry);
      const selectedInput = this.info.querySelector('form[action*="/cart/add"] input[name="id"]');
      this.load(selectedInput ? selectedInput.value : this.dataset.variantId);
    }

    disconnectedCallback() {
      this.controller?.abort();
      window.clearTimeout(this.timeout);
      this.info?.removeEventListener('change', this.onVariantChange);
      this.retry?.removeEventListener('click', this.onRetry);
      this.initialized = false;
      this.requestSequence = (this.requestSequence || 0) + 1;
    }

    parse(payload, variantId) {
      const variant = payload.data?.node;
      const components = variant?.components;
      if (payload.errors || variant?.id !== `gid://shopify/ProductVariant/${variantId}` ||
          variant?.product?.id !== `gid://shopify/Product/${this.dataset.productId}` ||
          !components?.pageInfo || components.pageInfo.hasNextPage ||
          !Array.isArray(components.nodes) || !components.nodes.length) {
        throw new Error('Incomplete bundle contents');
      }
      const products = new Map();
      components.nodes.forEach((component) => {
        const product = component.productVariant?.product;
        if (!product?.id || !product.title || !Number.isInteger(component.quantity) || component.quantity < 1) {
          throw new Error('Invalid bundle component');
        }
        const entry = products.get(product.id) || { title: product.title, quantity: 0 };
        entry.quantity += component.quantity;
        products.set(product.id, entry);
      });
      return [...products.values()].sort((a, b) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }));
    }

    show(products) {
      const key = JSON.stringify(products);
      if (this.displayedProductsKey !== key) {
        this.list.replaceChildren();
        products.forEach((product) => {
          const row = document.createElement('li');
          const quantity = document.createElement('span');
          const name = document.createElement('span');
          quantity.textContent = `${product.quantity} ×`;
          name.textContent = product.title;
          row.append(quantity, name);
          this.list.appendChild(row);
        });
        this.displayedProductsKey = key;
      }
      this.list.hidden = false;
      this.style.minHeight = '';
      this.status.hidden = true;
      this.retry.hidden = true;
      this.setAttribute('aria-busy', 'false');
    }

    async load(value, force = false) {
      const variantId = String(value || '');
      const cached = this.cache.get(variantId);
      if (!force && this.activeVariantId === variantId && this.loading) return;
      const sequence = this.requestSequence = (this.requestSequence || 0) + 1;
      this.activeVariantId = variantId;
      this.controller?.abort();
      window.clearTimeout(this.timeout);
      this.loading = false;
      if (!force && cached && cached.expiresAt > Date.now()) {
        this.show(cached.products);
        return;
      }
      // Keep the surrounding product page steady while unverified contents are hidden.
      this.style.minHeight = Math.ceil(this.getBoundingClientRect?.().height || 0) + 'px';
      this.list.hidden = true;
      this.list.replaceChildren();
      this.displayedProductsKey = null;
      this.retry.hidden = true;
      this.status.hidden = false;
      if (!/^\d+$/.test(variantId)) {
        this.status.textContent = 'Included coffees unavailable for this selection.';
        this.setAttribute('aria-busy', 'false');
        return;
      }
      this.loading = true;
      this.status.textContent = 'Loading included coffees…';
      this.setAttribute('aria-busy', 'true');
      const controller = this.controller = new AbortController();
      const timeout = this.timeout = window.setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch('/api/2026-07/graphql.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
          cache: 'no-store',
          signal: controller.signal,
          body: JSON.stringify({ query, variables: {
            id: `gid://shopify/ProductVariant/${variantId}`, country: this.dataset.country || null,
          } }),
        });
        if (!response.ok) throw new Error('Bundle lookup failed');
        const payload = await response.json();
        const products = this.parse(payload, variantId);
        if (sequence !== this.requestSequence || !this.isConnected) return;
        const expiresAt = Date.now() + 60000;
        this.cache.set(variantId, { products, expiresAt });
        const product = payload.data.node.product;
        const siblings = product.variants?.nodes;
        (Array.isArray(siblings) ? siblings : []).forEach((sibling) => {
          if (!sibling) return;
          const id = String(sibling.id || '').split('/').pop();
          const existing = this.cache.get(id);
          if (!/^\d+$/.test(id) || (existing && existing.expiresAt > Date.now())) return;
          try {
            const siblingProducts = this.parse({ data: { node: { ...sibling, product: { id: product.id } } } }, id);
            this.cache.set(id, { products: siblingProducts, expiresAt });
          } catch (error) { /* An invalid sibling does not affect the selected recipe. */ }
        });
        this.show(products);
      } catch (error) {
        if (sequence !== this.requestSequence || !this.isConnected) return;
        this.status.textContent = 'Coffee list unavailable right now.';
        this.style.minHeight = '';
        this.retry.hidden = false;
      } finally {
        window.clearTimeout(timeout);
        if (sequence === this.requestSequence) {
          this.loading = false;
          this.setAttribute('aria-busy', 'false');
        }
      }
    }
  }

  customElements.define('sampler-product-contents', SamplerProductContents);
})();
